import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { ref, uploadBytes, getDownloadURL, uploadString } from 'firebase/storage';
import { storage } from '../firebase';

/**
 * DesignSessionContext
 * Manages design session state across the order flow for custom design pages.
 * Handles session creation, design data storage, and Firebase persistence.
 */

const DesignSessionContext = createContext(null);

const STORAGE_KEY = 'designSession';

// Generate unique session ID
const generateSessionId = () => {
    const timestamp = Date.now().toString(36);
    const random = Math.random().toString(36).substring(2, 8);
    return `ds_${timestamp}_${random}`;
};

export function DesignSessionProvider({ children }) {
    // Session state
    const [sessionId, setSessionId] = useState(null);
    const [productId, setProductId] = useState(null);
    const [productSlug, setProductSlug] = useState(null);
    const [productName, setProductName] = useState(null);
    const [productPrice, setProductPrice] = useState(0);
    const [priceCurrency, setPriceCurrency] = useState('LKR');
    const [designData, setDesignData] = useState(null);
    const [designPreviewUrl, setDesignPreviewUrl] = useState(null);
    const [isDesignComplete, setIsDesignComplete] = useState(false);
    const [orderData, setOrderData] = useState(null); // For 'after' flow

    // Loading states
    const [isSaving, setIsSaving] = useState(false);

    // Restore session from localStorage on mount
    useEffect(() => {
        try {
            const savedSession = localStorage.getItem(STORAGE_KEY);
            if (savedSession) {
                const parsed = JSON.parse(savedSession);
                // Check if session is still valid (within 24 hours)
                const sessionAge = Date.now() - parsed.createdAt;
                const maxAge = 24 * 60 * 60 * 1000; // 24 hours

                if (sessionAge < maxAge) {
                    setSessionId(parsed.sessionId);
                    setProductId(parsed.productId);
                    setProductSlug(parsed.productSlug);
                    setProductName(parsed.productName);
                    setProductPrice(parsed.productPrice);
                    setPriceCurrency(parsed.priceCurrency);
                    setDesignData(parsed.designData);
                    setDesignPreviewUrl(parsed.designPreviewUrl);
                    setIsDesignComplete(parsed.isDesignComplete);
                    setOrderData(parsed.orderData);
                } else {
                    // Session expired, clear it
                    localStorage.removeItem(STORAGE_KEY);
                }
            }
        } catch (error) {
            console.error('Error restoring design session:', error);
            localStorage.removeItem(STORAGE_KEY);
        }
    }, []);

    // Save session to localStorage whenever it changes
    useEffect(() => {
        if (sessionId) {
            const sessionData = {
                sessionId,
                productId,
                productSlug,
                productName,
                productPrice,
                priceCurrency,
                designData,
                designPreviewUrl,
                isDesignComplete,
                orderData,
                createdAt: Date.now()
            };
            localStorage.setItem(STORAGE_KEY, JSON.stringify(sessionData));
        }
    }, [sessionId, productId, productSlug, productName, productPrice, priceCurrency, designData, designPreviewUrl, isDesignComplete, orderData]);

    /**
     * Initialize a new design session for a product
     */
    const initSession = useCallback((product) => {
        const newSessionId = generateSessionId();
        setSessionId(newSessionId);
        setProductId(product.id);
        setProductSlug(product.slug);
        setProductName(product.name);
        setProductPrice(product.price || 0);
        setPriceCurrency(product.priceCurrency || 'LKR');
        setDesignData(null);
        setDesignPreviewUrl(null);
        setIsDesignComplete(false);
        setOrderData(null);

        return newSessionId;
    }, []);

    /**
     * Update the design data
     */
    const updateDesignData = useCallback((data) => {
        setDesignData(data);
    }, []);

    /**
     * Mark design as complete
     */
    const completeDesign = useCallback(() => {
        setIsDesignComplete(true);
    }, []);

    /**
     * Store order data for 'after' flow (when design comes after order details)
     */
    const storeOrderData = useCallback((data) => {
        setOrderData(data);
    }, []);

    /**
     * Upload a preview image for the design
     */
    const uploadPreviewImage = useCallback(async (imageBlob, filename = 'preview.png') => {
        if (!sessionId) {
            throw new Error('No active design session');
        }

        try {
            const path = `designs/${sessionId}/${filename}`;
            const storageRef = ref(storage, path);

            if (imageBlob instanceof Blob) {
                await uploadBytes(storageRef, imageBlob);
            } else if (typeof imageBlob === 'string' && imageBlob.startsWith('data:')) {
                // Handle base64 data URL
                await uploadString(storageRef, imageBlob, 'data_url');
            } else {
                throw new Error('Invalid image format');
            }

            const url = await getDownloadURL(storageRef);
            setDesignPreviewUrl(url);
            return url;
        } catch (error) {
            console.error('Error uploading preview image:', error);
            throw error;
        }
    }, [sessionId]);

    /**
     * Upload an asset image (e.g., user-uploaded photos for the design)
     */
    const uploadAssetImage = useCallback(async (file, customName = null) => {
        if (!sessionId) {
            throw new Error('No active design session');
        }

        try {
            const filename = customName || `asset_${Date.now()}_${file.name}`;
            const path = `designs/${sessionId}/assets/${filename}`;
            const storageRef = ref(storage, path);

            await uploadBytes(storageRef, file);
            const url = await getDownloadURL(storageRef);

            return {
                storagePath: path,
                url,
                filename: file.name
            };
        } catch (error) {
            console.error('Error uploading asset image:', error);
            throw error;
        }
    }, [sessionId]);

    /**
     * Save the complete design data to Firebase Storage
     * Returns the URL to the design.json file
     */
    const saveToFirebase = useCallback(async () => {
        if (!sessionId || !designData) {
            throw new Error('No design data to save');
        }

        setIsSaving(true);
        try {
            const designBundle = {
                sessionId,
                productId,
                productSlug,
                productName,
                designData,
                previewUrl: designPreviewUrl,
                savedAt: new Date().toISOString()
            };

            const jsonString = JSON.stringify(designBundle, null, 2);
            const path = `designs/${sessionId}/design.json`;
            const storageRef = ref(storage, path);

            await uploadString(storageRef, jsonString);
            const designUrl = await getDownloadURL(storageRef);

            return {
                designUrl,
                previewUrl: designPreviewUrl,
                sessionId
            };
        } catch (error) {
            console.error('Error saving design to Firebase:', error);
            throw error;
        } finally {
            setIsSaving(false);
        }
    }, [sessionId, productId, productSlug, productName, designData, designPreviewUrl]);

    /**
     * Clear the current session
     */
    const clearSession = useCallback(() => {
        setSessionId(null);
        setProductId(null);
        setProductSlug(null);
        setProductName(null);
        setProductPrice(0);
        setPriceCurrency('LKR');
        setDesignData(null);
        setDesignPreviewUrl(null);
        setIsDesignComplete(false);
        setOrderData(null);
        localStorage.removeItem(STORAGE_KEY);
    }, []);

    /**
     * Check if there's an active session for a specific product
     */
    const hasSessionForProduct = useCallback((productIdToCheck) => {
        return sessionId && productId === productIdToCheck;
    }, [sessionId, productId]);

    const value = {
        // Session data
        sessionId,
        productId,
        productSlug,
        productName,
        productPrice,
        priceCurrency,
        designData,
        designPreviewUrl,
        isDesignComplete,
        orderData,

        // Loading states
        isSaving,

        // Methods
        initSession,
        updateDesignData,
        completeDesign,
        storeOrderData,
        uploadPreviewImage,
        uploadAssetImage,
        saveToFirebase,
        clearSession,
        hasSessionForProduct
    };

    return (
        <DesignSessionContext.Provider value={value}>
            {children}
        </DesignSessionContext.Provider>
    );
}

/**
 * Hook to access the design session context
 */
export function useDesignSession() {
    const context = useContext(DesignSessionContext);
    if (!context) {
        throw new Error('useDesignSession must be used within a DesignSessionProvider');
    }
    return context;
}

export default DesignSessionContext;
